import { useMemo, useState } from "react";
import {
  CERTIFICAT,
  CERTIFICATE_GROUPS,
  CODESIGNAL_PATH_ID,
  localizeCertificate,
} from "@data/certificat";
import useTranslations from "@hooks/useTranslations";
import { useLanguage } from "@contexts/LanguageContext";
import { ICONS } from "@utils/icons";
import "./Certificat.css";

const FILTER_KEYS = ["all", "Sololearn", "CodeSignal", "autres"];

const PATH_PARENT_ID = CERTIFICATE_GROUPS[CODESIGNAL_PATH_ID]?.parentCertificateId;

const isPathChild = (item) =>
  item.type === "course" && item.groupId === CODESIGNAL_PATH_ID;

const Certificat = () => {
  const t = useTranslations();
  const { lang } = useLanguage();
  const [activeFilter, setActiveFilter] = useState("all");
  const [isGroupOpen, setGroupOpen] = useState(false);

  const { parents, children } = useMemo(() => {
    const matchesFilter = (item) => {
      if (activeFilter === "all") return true;
      if (activeFilter === "autres") {
        return item.issuer !== "Sololearn" && item.issuer !== "CodeSignal";
      }
      return item.issuer === activeFilter;
    };

    const visible = CERTIFICAT.filter(matchesFilter).map((item) =>
      localizeCertificate(item, lang)
    );

    return {
      parents: visible.filter((item) => !isPathChild(item)),
      children: visible.filter(isPathChild),
    };
  }, [activeFilter, lang]);

  const renderCard = (element, variant) => {
    const isPrimary = variant === "primary";

    return (
      <li
        className={`certificat-card${isPrimary ? " certificat-card--primary" : ""}`}
        key={element.id}
      >
        <div className="certificat-card-info">
          {isPrimary && (
            <span className="certificat-card-badge">
              <i className={ICONS.award} />
              {t.certificat.finalLabel}
            </span>
          )}
          <h6 className="card-title">{element.nom}</h6>
          <span className="card-organisation">
            {element.etablissement}
            <span className="certificat-dot" aria-hidden="true">
              ·
            </span>
            <span className="card-subtitle">{element.dateObtention}</span>
          </span>
          {element.summaryText && (
            <p className="card-summary">{element.summaryText}</p>
          )}
        </div>
        {element.lien ? (
          <div className="certificat-card-action">
            <a
              href={element.lien}
              target="_blank"
              rel="noreferrer"
              className="form-btn"
            >
              {t.certificat.view} <i className={ICONS.chevronRight} />
            </a>
          </div>
        ) : null}
      </li>
    );
  };

  const hasParent = parents.some((item) => item.id === PATH_PARENT_ID);

  return (
    <div className="section-wrapper">
      <div className="bostami-page-title-wrap mb-20">
        <h1 className="page-title">{t.cv.certifications}</h1>
      </div>

      <div className="certificat-filters">
        {FILTER_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            className={`certificat-filter-btn ${activeFilter === key ? "active" : ""}`}
            aria-pressed={activeFilter === key}
            onClick={() => setActiveFilter(key)}
          >
            {t.certificat.filters[key]}
          </button>
        ))}
      </div>

      {parents.length === 0 && children.length === 0 && (
        <p className="certificat-empty">{t.certificat.empty}</p>
      )}

      <ul className="certificat-list">
        {parents.map((element) => {
          const isPathParent = element.id === PATH_PARENT_ID;

          if (!isPathParent) return renderCard(element, "default");

          return (
            <li className="certificat-group-wrapper" key={element.id}>
              <ul className="certificat-list">
                {renderCard(element, "primary")}
              </ul>

              {children.length > 0 && (
                <>
                  <button
                    type="button"
                    className="certificat-toggle-btn"
                    aria-expanded={isGroupOpen}
                    onClick={() => setGroupOpen((open) => !open)}
                  >
                    <i className={isGroupOpen ? ICONS.chevronUp : ICONS.chevronDown} />
                    {isGroupOpen ? t.certificat.hidePath : t.certificat.showPath}
                  </button>

                  {isGroupOpen && (
                    <ul className="certificat-list certificat-children">
                      {children.map((child) => renderCard(child, "default"))}
                    </ul>
                  )}
                </>
              )}
            </li>
          );
        })}

        {/*
          Quand le filtre masque le certificat final du parcours, ses cours
          enfants restent affichés à plat plutôt que d'être perdus.
        */}
        {!hasParent && children.map((child) => renderCard(child, "default"))}
      </ul>
    </div>
  );
};

export default Certificat;
